import {useEffect, useMemo, useState} from "react";
import {useQueries} from "@tanstack/react-query";
import {Box, Typography} from "@mui/material";
import {useSalesRegions} from "../../hooks/useSalesRegions";
import {useSalesTerritories} from "../../hooks/useSalesTerritories";
import {getSalesTerritories} from "../../services/IsfaService";

const labelSx = {fontWeight: 700, fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary", mb: 0.5};

// Strip the " (lastName)" display decoration useSalesTerritories appends, to recover the raw
// territory name the API expects.
const rawTerritoryName = (option) =>
    option.salesRepLastName ? option.name.replace(` (${option.salesRepLastName})`, "") : option.name;

// Multi sales-territory picker shared by the Create User wizard (Step 4) and the View/Edit User page
// (Additional Details) so both edit territories the same way.
//
// Edit mode: region tabs (each with a per-region selected count) → click-to-toggle territory pills
// for the active region → a "selected" pool grouped by source region, each removable. View mode: a
// read-only list of the selected territories.
//
// Selection shape: {id, name, label, region, regionName} — id is the territory id (the selection
// identity), name is the raw territory name, label is the decorated display string. Territories
// loaded from the server arrive as bare {id} (no name/region) — they bucket under "Assigned" in the
// pool and are resolved by id against every region's territory list in both edit and view mode,
// without requiring any tab to be opened.
const MultiTerritoryPicker = ({editMode = true, businessUnitCode, userId, value, onChange}) => {
    const [activeRegion, setActiveRegion] = useState("");
    const regions = useSalesRegions(businessUnitCode, userId, !!businessUnitCode);
    const territories = useSalesTerritories(businessUnitCode, activeRegion, !!businessUnitCode && !!activeRegion && editMode);

    const selected = value ?? [];
    const selectedIds = useMemo(() => new Set(selected.map((t) => t.id)), [selected]);

    // Per-region selected count (for the tab badges).
    const countByRegion = useMemo(() => {
        const m = new Map();
        selected.forEach((t) => m.set(t.region, (m.get(t.region) ?? 0) + 1));
        return m;
    }, [selected]);

    // Accumulated selections grouped by source region (for the selected pool / view list).
    const byRegion = useMemo(() => {
        const m = new Map();
        selected.forEach((t) => {
            const key = t.region ?? "";
            if (!m.has(key)) m.set(key, []);
            m.get(key).push(t);
        });
        return m;
    }, [selected]);

    // Server-loaded territories arrive as bare {id} (no region), so they wouldn't count toward any
    // region tab. While any selected territory still lacks a region, fetch every region's territory
    // list (shared react-query cache — the active tab isn't double-fetched) and backfill the region
    // onto those items by id, so the tab counts and grouping reflect them without manual clicking. An
    // id match (rather than name) is required — a territory name can repeat across regions, and a name
    // match would attribute it to the wrong region tab.
    const needResolve = selected.some((t) => !t.region);
    const regionResults = useQueries({
        queries: (needResolve ? regions : []).map((r) => ({
            queryKey: ["salesTerritories", businessUnitCode, r.id],
            queryFn: () => getSalesTerritories(businessUnitCode, r.id),
            enabled: needResolve && !!businessUnitCode,
            retry: false,
        })),
    });
    const resolvedById = useMemo(() => {
        const m = new Map();
        if (!needResolve) return m;
        regions.forEach((r, i) => {
            const rows = regionResults[i]?.data;
            if (Array.isArray(rows)) {
                rows.forEach((o) => {
                    m.set(o.id, {
                        region: r.id,
                        regionName: r.name,
                        name: o.name,
                        label: o.salesRepLastName ? `${o.name} (${o.salesRepLastName})` : o.name,
                    });
                });
            }
        });
        return m;
    }, [needResolve, regions, regionResults]);

    // View-mode display list: fills in region/regionName/name/label from resolvedById for any
    // selection that doesn't already carry a region, without mutating the parent's data.
    const viewList = useMemo(
        () =>
            selected.map((t) => {
                if (t.region || !resolvedById.has(t.id)) return t;
                const info = resolvedById.get(t.id);
                return {...t, region: info.region, regionName: info.regionName, name: info.name, label: t.label || info.label};
            }),
        [selected, resolvedById]
    );

    // Edit-mode only: backfill the resolved region onto the selections themselves so the tab
    // counts/grouping reflect them. Must never run in view mode — onChange there would dirty a
    // draft the user never opened for editing.
    useEffect(() => {
        if (!editMode || !needResolve || resolvedById.size === 0) return;
        let changed = false;
        const next = selected.map((t) => {
            if (!t.region && resolvedById.has(t.id)) {
                changed = true;
                const info = resolvedById.get(t.id);
                return {...t, region: info.region, regionName: info.regionName, name: info.name, label: t.label || info.label};
            }
            return t;
        });
        if (changed) onChange(next);
    }, [editMode, needResolve, resolvedById, selected, onChange]);

    // ---- view mode: read-only ----
    if (!editMode) {
        return (
            <Box sx={{mt: 3}}>
                <Typography sx={labelSx}>Sales Territories</Typography>
                {viewList.length === 0 ? (
                    <Typography sx={{fontSize: "0.9rem", color: "text.secondary", fontStyle: "italic"}}>—</Typography>
                ) : (
                    <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.5}}>
                        {viewList.map((t) => (
                            <Box key={t.id} sx={{px: 1.25, py: 0.5, borderRadius: "99px", border: 1, borderColor: "divider", fontSize: "0.78rem"}}>
                                {(t.label || t.name || "") + (t.regionName ? ` (${t.regionName})` : "")}
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        );
    }

    const toggle = (option) => {
        if (selectedIds.has(option.id)) {
            onChange(selected.filter((t) => t.id !== option.id));
        } else {
            const name = rawTerritoryName(option);
            const regionName = regions.find((r) => r.id === activeRegion)?.name ?? activeRegion;
            onChange([...selected, {id: option.id, name, label: option.name, region: activeRegion, regionName}]);
        }
    };
    const remove = (id) => onChange(selected.filter((t) => t.id !== id));

    return (
        <Box sx={{mt: 3, border: 1, borderColor: "divider", borderRadius: "8px", p: 2}}>
            <Typography sx={labelSx}>Sales Territories</Typography>

            {/* Region tabs */}
            <Box sx={{display: "flex", gap: 0.25, borderBottom: 1, borderColor: "divider", mb: 2, overflowX: "auto"}}>
                {regions.map((r) => {
                    const active = r.id === activeRegion;
                    const count = countByRegion.get(r.id) ?? 0;
                    return (
                        <Box
                            key={r.id}
                            onClick={() => setActiveRegion(r.id)}
                            sx={{
                                display: "flex", alignItems: "center", gap: 0.75,
                                px: 2, py: 1, mb: "-1px", cursor: "pointer", whiteSpace: "nowrap",
                                fontSize: "0.8rem", fontWeight: active ? 700 : 500,
                                color: active ? "secondary.main" : "text.secondary",
                                borderBottom: 2, borderColor: active ? "secondary.main" : "transparent",
                            }}
                        >
                            {r.name}
                            {count > 0 && (
                                <Box component="span" sx={{fontSize: "0.65rem", fontWeight: 700, px: 0.75, py: "1px", borderRadius: "99px", backgroundColor: "rgba(0,163,255,0.1)", color: "secondary.main"}}>
                                    {count}
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>

            {/* Territory pills for the active region */}
            <Box sx={{minHeight: 40, mb: 2}}>
                {!activeRegion ? (
                    <Typography sx={{fontSize: "0.8rem", color: "text.secondary", fontStyle: "italic", py: 1}}>
                        Select a region above
                    </Typography>
                ) : (
                    <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.75}}>
                        {territories.map((option) => {
                            const isSel = selectedIds.has(option.id);
                            return (
                                <Box
                                    key={option.id}
                                    onClick={() => toggle(option)}
                                    sx={{
                                        px: 1.5, py: 0.75, borderRadius: "99px", cursor: "pointer",
                                        fontSize: "0.78rem", fontWeight: isSel ? 700 : 500, border: 1,
                                        borderColor: isSel ? "secondary.main" : "divider",
                                        backgroundColor: isSel ? "rgba(0,163,255,0.1)" : "transparent",
                                        color: isSel ? "secondary.main" : "text.secondary",
                                        "&:hover": {backgroundColor: isSel ? "rgba(0,163,255,0.1)" : "rgba(0,0,0,0.04)"},
                                    }}
                                >
                                    {option.name}
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Box>

            {/* Selected pool — grouped by source region */}
            <Box sx={{borderTop: 1, borderColor: "divider", pt: 2}}>
                <Typography sx={{...labelSx, mb: 1.5}}>
                    Selected Territories {selected.length > 0 ? `(${selected.length})` : ""}
                </Typography>
                {selected.length === 0 ? (
                    <Typography sx={{fontSize: "0.8rem", color: "text.secondary", fontStyle: "italic"}}>
                        No territories selected yet
                    </Typography>
                ) : (
                    [...byRegion.entries()].map(([reg, list]) => (
                        <Box key={reg || "_"} sx={{mb: 1.5}}>
                            <Typography sx={{fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary", mb: 0.75}}>
                                {list[0]?.regionName || reg || "Assigned"}
                            </Typography>
                            <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.75}}>
                                {list.map((t) => (
                                    <Box key={t.id} sx={{display: "inline-flex", alignItems: "center", gap: 0.5, pl: 1.25, pr: 0.5, py: 0.5, borderRadius: "99px", border: 1, borderColor: "divider", fontSize: "0.78rem"}}>
                                        {t.label || t.name || ""}
                                        <Box
                                            component="button"
                                            onClick={() => remove(t.id)}
                                            aria-label={`Remove ${t.label || t.name || ""}`}
                                            sx={{
                                                width: 16, height: 16, borderRadius: "50%", border: "none", cursor: "pointer",
                                                display: "flex", alignItems: "center", justifyContent: "center",
                                                fontSize: "0.6rem", lineHeight: 1, p: 0,
                                                backgroundColor: "rgba(0,0,0,0.06)", color: "text.secondary",
                                                "&:hover": {backgroundColor: "error.main", color: "white"},
                                            }}
                                        >
                                            ✕
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    ))
                )}
            </Box>
        </Box>
    );
};

export default MultiTerritoryPicker;
